import { type GetStaticPropsResult, type GetStaticPathsResult } from 'next'
import dynamic from 'next/dynamic'
import { type ParsedUrlQuery } from 'querystring'

import { customPageModuleQuery } from '@data/sanity/queries/page'
import { type SanityCustomPage } from '@data/sanity/queries/types/page'
import { type SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getShopifyDomain } from '@lib/shopify/client'
import { getSanityClient } from '@lib/sanity/client'
import {
  defaultModuleQueryParameters,
  getAllSlugs,
  getPage,
} from '@lib/sanity/page'
import { parseOptionalParameters } from '@lib/helpers'
import { type Locale, getAllLocales } from '@lib/language'
import {
  type BasePageProps,
  getNonNullValues,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import { type Reviews, getReviews } from '@lib/review'
import {
  type CustomStaticPropsContext,
  type StaticPathWithSlugs,
} from '@lib/routes'
import { getVideoObjectSchemas } from '@lib/schema/video'
import { getWebPageSchema } from '@lib/schema/web'

import Layout from '@components/layout'
import Modules from '@modules/modules'

const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type PageProps = BasePageProps & {
  slugs: string[]
  page: SanityCustomPage
  site: SanitySiteFragment
  reviews?: Reviews
}

const Page = ({ draftMode, locale, slugs, page, site, reviews }: PageProps) => {
  const webPageSchema = getWebPageSchema(page, site)
  const videoObjectSchemas = getVideoObjectSchemas(page)
  const schemas = [webPageSchema, ...videoObjectSchemas]

  return (
    <Layout page={page} site={site} schemas={schemas} draftMode={draftMode}>
      {!draftMode && <Modules modules={page.modules} reviews={reviews} />}
      {draftMode && (
        <PreviewModules
          modules={page.modules}
          reviews={reviews}
          query={customPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
            pageSlugs: slugs,
          }}
        />
      )}
    </Layout>
  )
}

interface PageParams extends ParsedUrlQuery {
  slugs: string[]
}

export async function getStaticProps(
  context: CustomStaticPropsContext<PageParams>,
): Promise<GetStaticPropsResult<PageProps>> {
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const slugs = parseOptionalParameters(context.params?.slugs) ?? []
  const page = await getPage(sanityClient, context.locale, slugs.join('/'))

  if (!page.page || page.page.type === 'template') {
    return {
      notFound: true,
    }
  }

  // Get Stamped.io reviews
  const shopifyDomain = getShopifyDomain(context.locale)
  const reviews = await getReviews(
    page.page.modules ?? [],
    shopifyDomain,
    page.site.generalSettings.stampedApiKey,
  )

  return {
    props: {
      ...getPagePropsFromContext(context),
      slugs,
      page: getNonNullValues(page.page),
      site: getNonNullValues(page.site),
      reviews,
    },
    revalidate: 60,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<PageParams>
> {
  if (process.env.NEXT_PUBLIC_APP_ENV === 'preview') {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }

  const sanityClient = getSanityClient()
  const allPages = await getAllSlugs(sanityClient, 'page')
  const allLocales = getAllLocales()
  const paths: StaticPathWithSlugs[] = allPages
    .map((page) => ({
      ...page,
      slug: page.slug.current,
      locale: page.locale as Locale,
    }))
    .filter((page) => allLocales.includes(page.locale))
    .filter((page) => !!page.slug)
    .map((page) => ({
      params: {
        slugs: page.slug.split('/').filter((slug) => !!slug),
      },
      locale: page.locale,
    }))

  return {
    paths,
    fallback: 'blocking',
  }
}

export default Page
